import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, RefreshCw, AlertTriangle, Eye } from "lucide-react";
import { currentBs, lastNBsMonths, bsMonthName } from "@/lib/bs-calendar";

/**
 * Phase 4 — Incentive Summary (Product Manager / Admin / Sales Head desktop).
 *
 * Shows the calc engine result row-per-promoter with totals + filters,
 * the no-rule approved-sales report, and Excel exports.
 *
 * Phase 6.1.B — Calendar toggle (AD / BS). When BS is selected, the page
 * queries the same endpoints with `bs_month=YYYY-MM` (BS year-month).
 */
export default function IncentiveSummary() {
  const today = new Date().toISOString().slice(0, 7);
  const [calendar, setCalendar] = useState("bs"); // 'ad' | 'bs' — BS is the new default per Phase 6.1
  const [month, setMonth] = useState(today);
  const nowBs = useMemo(() => currentBs(), []);
  const [bsMonth, setBsMonth] = useState(
    nowBs ? `${nowBs.bs_year}-${String(nowBs.bs_month).padStart(2, "0")}` : today,
  );
  const bsOptions = useMemo(
    () => (nowBs ? lastNBsMonths(13, nowBs.bs_year, nowBs.bs_month) : []),
    [nowBs],
  );
  const periodKey = calendar === "bs" ? bsMonth : month;
  const periodLabel = calendar === "bs"
    ? (() => {
        const [y, m] = bsMonth.split("-");
        return `${bsMonthName(Number(m))} ${y} (BS)`;
      })()
    : `${month} (AD)`;
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [noRule, setNoRule] = useState({ rows: [], count: 0 });
  const [drill, setDrill] = useState(null); // {promoter_id}

  const load = async () => {
    setLoading(true);
    try {
      const params = calendar === "bs" ? { bs_month: bsMonth } : { month };
      const [s, n] = await Promise.all([
        api.get("/promoter/calc/summary", { params }),
        api.get("/promoter/calc/no-rule-sales", { params }),
      ]);
      setData(s.data);
      setNoRule(n.data);
    } catch (e) {
      toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [calendar, month, bsMonth]);

  const buildQs = (extra = {}) => {
    const sp = new URLSearchParams(extra);
    if (calendar === "bs") sp.set("bs_month", bsMonth);
    else sp.set("month", month);
    return sp.toString();
  };

  const exportXlsx = (kind) => {
    const token = localStorage.getItem("access_token");
    const qs = buildQs({ kind });
    fetch(`${API}/promoter/calc/export.xlsx?${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => { if (!r.ok) throw new Error("export"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `incentive_${kind}_${calendar}_${periodKey}.xlsx`; a.click();
        URL.revokeObjectURL(url);
      }).catch(() => toast.error("Could not export"));
  };

  const exportVatReconciliation = () => {
    // VAT reconciliation is keyed by AD month — the underlying VAT books are AD-based.
    // When user is in BS mode, fall back to current AD month for the VAT export and explain.
    const token = localStorage.getItem("access_token");
    fetch(`${API}/promoter/exports/vat-reconciliation.xlsx?month=${month}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => { if (!r.ok) throw new Error("export"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `vat_reconciliation_${month}.xlsx`; a.click();
        URL.revokeObjectURL(url);
      }).catch(() => toast.error("Could not export VAT reconciliation"));
  };

  return (
    <div className="space-y-4" data-testid="incentive-summary-page">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Incentive Summary</h1>
          <p className="text-sm text-slate-500 mt-1">
            Calculated only from approved sales × approved+active rules / targets / slabs.
            Draft, pending, rejected, or inactive records have zero effect.
            <br />
            <span className="text-slate-700 font-medium">Reporting period: {periodLabel}</span>
          </p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Calendar</Label>
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="calendar-toggle">
              <button
                type="button"
                className={`h-8 px-3 text-xs ${calendar === "bs" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
                onClick={() => setCalendar("bs")}
                data-testid="cal-bs">
                BS
              </button>
              <button
                type="button"
                className={`h-8 px-3 text-xs border-l border-slate-200 ${calendar === "ad" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
                onClick={() => setCalendar("ad")}
                data-testid="cal-ad">
                AD
              </button>
            </div>
          </div>
          {calendar === "ad" ? (
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600">AD Month</Label>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                     className="h-8 w-40" data-testid="incentive-month" />
            </div>
          ) : (
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600">BS Month</Label>
              <select
                value={bsMonth}
                onChange={(e) => setBsMonth(e.target.value)}
                className="h-8 w-44 rounded-md border border-slate-200 px-2 text-sm bg-white"
                data-testid="incentive-bs-month">
                {bsOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}
          <Button variant="outline" onClick={load} data-testid="incentive-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button variant="outline" onClick={() => exportXlsx("summary")} data-testid="incentive-export-summary">
            <Download size={14} className="mr-1" /> Summary
          </Button>
          <Button variant="outline" onClick={() => exportXlsx("no-rule")} data-testid="incentive-export-norule">
            <Download size={14} className="mr-1" /> No-rule
          </Button>
          <Button variant="outline" onClick={exportVatReconciliation} data-testid="incentive-export-vat">
            <Download size={14} className="mr-1" /> VAT Recon
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-3" data-testid="incentive-totals">
        <Stat label="Net sales (excl. VAT)" value={`Rs. ${Number(data.totals.approved_sales_value || 0).toLocaleString()}`} testid="tot-sales" />
        <Stat label="Raw incentive" value={`Rs. ${Number(data.totals.raw_incentive || 0).toLocaleString()}`} testid="tot-raw" />
        <Stat label="Payable incentive" value={`Rs. ${Number(data.totals.payable_incentive || 0).toLocaleString()}`} testid="tot-payable" tone="emerald" />
      </div>
      <div className="grid grid-cols-2 gap-3 text-[11px] text-slate-500" data-testid="incentive-vat-totals">
        <div>Gross invoiced (incl. VAT): <span className="font-mono text-slate-700">Rs. {Number(data.totals.approved_sales_gross_value || 0).toLocaleString()}</span></div>
        <div>VAT collected: <span className="font-mono text-slate-700">Rs. {Number(data.totals.approved_vat_value || 0).toLocaleString()}</span></div>
      </div>
      {data.vat_note && (
        <div className="text-[11px] text-slate-500 italic" data-testid="incentive-vat-note">{data.vat_note}</div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="incentive-table">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2 sticky left-0 bg-slate-50">Promoter</th>
                  <th className="text-left px-3 py-2">Dealer / Area</th>
                  <th className="text-right px-3 py-2">Units</th>
                  <th className="text-right px-3 py-2">Gross (Rs)</th>
                  <th className="text-right px-3 py-2">VAT (Rs)</th>
                  <th className="text-right px-3 py-2">Net Sales (Rs)</th>
                  <th className="text-right px-3 py-2">Target (Rs)</th>
                  <th className="text-right px-3 py-2">Achv %</th>
                  <th className="text-right px-3 py-2">Raw</th>
                  <th className="text-right px-3 py-2">Slab</th>
                  <th className="text-right px-3 py-2">Bonus</th>
                  <th className="text-right px-3 py-2">Payable</th>
                  <th className="text-left px-3 py-2">Status / Reason</th>
                  <th className="text-right px-3 py-2">Drill</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={14} className="text-center py-6 text-slate-400">Loading…</td></tr>}
                {!loading && data.rows.length === 0 && (
                  <tr><td colSpan={14} className="text-center py-6 text-slate-400" data-testid="incentive-empty">
                    No promoter sales or targets in this month.
                  </td></tr>
                )}
                {!loading && data.rows.map((r) => {
                  const reason = inferZeroReason(r);
                  return (
                  <tr key={r.promoter_id} className="border-t border-slate-100 hover:bg-slate-50/60"
                      data-testid={`incentive-row-${(r.promoter_promoter_id || r.promoter_id).slice(0, 12)}`}>
                    <td className="px-3 py-2 sticky left-0 bg-white">{r.promoter_name}<div className="text-[10px] text-slate-400">{r.promoter_promoter_id}</div></td>
                    <td className="px-3 py-2 text-slate-600">{r.dealer || "—"}<div className="text-[10px] text-slate-500">{r.area || ""}</div></td>
                    <td className="px-3 py-2 text-right">{r.approved_units}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{Number(r.approved_sales_gross_value || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{Number(r.approved_vat_value || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-medium">{Number(r.approved_sales_value).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{Number(r.target_amount || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">
                      <AchPill pct={r.achievement_pct} hasTarget={(r.target_amount || 0) > 0} />
                    </td>
                    <td className="px-3 py-2 text-right">{Number(r.raw_incentive).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{Number(r.slab_payout).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{Number(r.bonus_amount).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-bold">{Number(r.payable_incentive).toLocaleString()}</td>
                    <td className="px-3 py-2">
                      {reason ? (
                        <span className="inline-flex items-center gap-1 text-[11px] bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded"
                              data-testid={`incentive-zero-reason-${(r.promoter_promoter_id || r.promoter_id).slice(0,12)}`}>
                          <AlertTriangle size={10} /> {reason}
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-[11px] text-emerald-700 font-semibold">payable</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => setDrill({ promoter_id: r.promoter_id, promoter_name: r.promoter_name })}
                              data-testid={`incentive-drill-${(r.promoter_promoter_id || r.promoter_id).slice(0, 12)}`}>
                        <Eye size={12} />
                      </Button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* No-rule report */}
      {noRule.count > 0 && (
        <Card data-testid="no-rule-card">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-600" />
              <div className="font-semibold text-slate-900">No-rule approved sales ({noRule.count})</div>
              <Badge className="bg-amber-100 text-amber-800">action needed</Badge>
            </div>
            <p className="text-xs text-slate-500">
              These approved sales did not match any active incentive rule for the month. Add or revise
              an incentive rule for the brand/category/model below — once approved, the next calc run will pick them up.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-2 py-1">Date</th>
                    <th className="text-left px-2 py-1">Promoter</th>
                    <th className="text-left px-2 py-1">Brand / Category / Model</th>
                    <th className="text-right px-2 py-1">Qty</th>
                    <th className="text-right px-2 py-1">Net (excl. VAT)</th>
                    <th className="text-right px-2 py-1">Gross</th>
                  </tr>
                </thead>
                <tbody>
                  {noRule.rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-2 py-1 font-mono">{r.sale_date}</td>
                      <td className="px-2 py-1">{r.promoter_name}</td>
                      <td className="px-2 py-1">{r.brand} / {r.product_category || "—"} / {r.product_model}</td>
                      <td className="px-2 py-1 text-right">{r.quantity}</td>
                      <td className="px-2 py-1 text-right font-semibold">{Number(r.net_sales_amount || 0).toLocaleString()}</td>
                      <td className="px-2 py-1 text-right text-slate-500">{Number(r.invoice_amount || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <DrillDialog state={drill} calendar={calendar} period={periodKey} onClose={() => setDrill(null)} />
    </div>
  );
}

function AchPill({ pct, hasTarget }) {
  if (!hasTarget) return <span className="text-slate-400 text-xs">—</span>;
  const cls = pct >= 100 ? "bg-emerald-100 text-emerald-800"
            : pct >= 70 ? "bg-amber-100 text-amber-800"
            : "bg-rose-100 text-rose-800";
  return <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold ${cls}`}>{Number(pct).toFixed(1)}%</span>;
}

function DrillDialog({ state, calendar, period, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!state) return;
    setData(null);
    const params = calendar === "bs" ? { bs_month: period } : { month: period };
    api.get(`/promoter/calc/promoter/${state.promoter_id}`, { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) }));
  }, [state, calendar, period]);
  if (!state) return null;
  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0" data-testid="incentive-drill-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 flex-shrink-0">
          <DialogTitle>{state.promoter_name} · {period} ({calendar.toUpperCase()})</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2 text-sm" data-testid="incentive-drill-scroll">
        {!data && <div className="text-sm text-slate-500">Loading…</div>}
        {data && (
          <div className="space-y-2 text-sm">
            {/* Zero-reason classifier */}
            {Number(data.payable_incentive || 0) === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs" data-testid="incentive-drill-zero-reason">
                <div className="font-semibold text-amber-900 flex items-center gap-1"><AlertTriangle size={12} /> Payable incentive is zero</div>
                <div className="text-amber-800 mt-1">{inferZeroReason({
                  payable_incentive: data.payable_incentive,
                  raw_incentive: data.raw_incentive,
                  approved_sales_value: data.approved_sales_value,
                  no_rule_value: data.no_rule_value,
                  target_amount: data.target?.sales_amount || 0,
                  slab_payout: data.slab_payout,
                  achievement_pct: data.achievement_pct,
                }) || "No payable incentive in this period."}</div>
                <ul className="mt-1 text-amber-700 text-[11px] list-disc list-inside">
                  {!data.target && <li>No approved target for this BS period.</li>}
                  {data.no_rule_value > 0 && <li>Some approved sales had no matching incentive rule (No-rule value Rs. {Number(data.no_rule_value).toLocaleString()}).</li>}
                  {data.target && (data.target.sales_amount || 0) > 0 && (data.slab_payout || 0) === 0 && (
                    <li>Achievement {Number(data.achievement_pct || 0).toFixed(1)}% did not reach any approved slab.</li>
                  )}
                </ul>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Row label="Net sales (excl. VAT)" value={`Rs. ${Number(data.approved_sales_value).toLocaleString()} (${data.approved_units} unit)`} />
              <Row label="Target (Net)" value={data.target ? `Rs. ${Number(data.target.sales_amount).toLocaleString()}` : "—"} />
              <Row label="Achievement" value={`${Number(data.achievement_pct).toFixed(2)}%`} />
              <Row label="Raw incentive" value={`Rs. ${Number(data.raw_incentive).toLocaleString()}`} />
              <Row label="Slab payout" value={data.slab ? `${data.slab_payout_pct}% → Rs. ${Number(data.slab_payout).toLocaleString()}` : "—"} />
              <Row label="Bonus" value={`Rs. ${Number(data.bonus_amount).toLocaleString()} (${data.bonus_source || "—"})`} />
              <Row label="Payable" value={`Rs. ${Number(data.payable_incentive).toLocaleString()}`} />
              <Row label="No-rule sales (Net)" value={`Rs. ${Number(data.no_rule_value).toLocaleString()} (${data.no_rule_units} unit)`} />
              <Row label="Gross invoiced" value={`Rs. ${Number(data.approved_sales_gross_value || 0).toLocaleString()}`} />
              <Row label="VAT collected" value={`Rs. ${Number(data.approved_vat_value || 0).toLocaleString()}`} />
            </div>
            <div className="font-semibold text-slate-900 pt-2">Sales trace</div>
            <div className="overflow-x-auto max-h-80 overflow-y-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">Date</th>
                    <th className="text-left px-2 py-1">Invoice</th>
                    <th className="text-left px-2 py-1">Brand / Model</th>
                    <th className="text-right px-2 py-1">Qty</th>
                    <th className="text-right px-2 py-1">Unit</th>
                    <th className="text-right px-2 py-1">Raw</th>
                    <th className="text-left px-2 py-1">Match</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sales_trace.map((t) => (
                    <tr key={t.sale_id} className="border-t border-slate-100">
                      <td className="px-2 py-1 font-mono">{t.sale_date}</td>
                      <td className="px-2 py-1 font-mono">{t.invoice_number}</td>
                      <td className="px-2 py-1">{t.brand} {t.model}</td>
                      <td className="px-2 py-1 text-right">{t.qty}</td>
                      <td className="px-2 py-1 text-right">{Number(t.unit_amount).toLocaleString()}</td>
                      <td className="px-2 py-1 text-right">{Number(t.raw).toLocaleString()}</td>
                      <td className="px-2 py-1">
                        {t.rule_match === "no_rule" ? <Badge className="bg-rose-100 text-rose-700">no rule</Badge>
                          : <span className="text-emerald-700 text-[10px]">{t.rule_match}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, testid, tone }) {
  const toneCls = { emerald: "text-emerald-700" }[tone] || "text-slate-900";
  return (
    <Card data-testid={testid}>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`font-heading text-xl font-bold mt-1 ${toneCls} truncate`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-900">{value}</div>
    </div>
  );
}

/** Iter42 — classify why payable incentive is zero, in priority order.
 *  Returns null when payable > 0.
 */
function inferZeroReason(r) {
  const payable = Number(r.payable_incentive || 0);
  if (payable > 0) return null;
  // 1. No approved/active incentive rule (raw=0 despite sales > 0)
  const raw = Number(r.raw_incentive || 0);
  const sales = Number(r.approved_sales_value || 0);
  const noRuleVal = Number(r.no_rule_value || 0);
  if (sales > 0 && raw === 0) {
    if (noRuleVal > 0) return "No incentive rule for these sales";
    return "No approved/active incentive rule";
  }
  // 2. Achievement % below the lowest slab threshold
  const target = Number(r.target_amount || 0);
  const slabPayout = Number(r.slab_payout || 0);
  const pct = Number(r.achievement_pct || 0);
  if (target > 0 && raw > 0 && slabPayout === 0 && pct > 0) {
    return `Achievement ${pct.toFixed(1)}% below lowest slab`;
  }
  // 3. No approved target for this BS period
  if (target === 0 && raw > 0 && slabPayout === 0) {
    return "No approved target for this period";
  }
  // 4. Approved slabs missing
  if (target > 0 && raw > 0 && slabPayout === 0 && pct >= 100) {
    return "Approved active slab missing";
  }
  // 5. BS month mismatch / rule period doesn't overlap
  if (sales > 0 && raw === 0 && noRuleVal === 0) {
    return "Rule period does not overlap BS month";
  }
  return "No incentive earned";
}
